import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../components/ui/button-old'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'

const SetPassword = () => {
  const { token, tenanturlname } = useParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { t } = useLanguage()

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState(true)

  // Validate token exists
  useEffect(() => {
    if (!token) {
      setTokenValid(false)
    }
  }, [token])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = t('resetPassword.errors.passwordRequired')
    } else {
      const passwordValidation = Validator.isValidPassword(formData.newPassword)
      if (!passwordValidation.valid) {
        newErrors.newPassword = t(
          `resetPassword.errors.${passwordValidation.errorCode}`,
        )
      }
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.errors.confirmRequired')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.errors.passwordMismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      await apiService.post('/api/kc/complete-migration', {
        migrationToken: token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        clienttenantid: tenant?.tenantid || 1011,
      })

      // Success
      setIsSuccess(true)

      // Redirect to signin after 3 seconds
      setTimeout(() => {
        const tenantUrlName = tenant?.tenanturlname || tenanturlname
        navigate(`/${tenantUrlName}/signin`)
      }, 3000)
    } catch (error) {
      console.error('Migration completion error:', error)

      // Handle API errors - apiService.handleError transforms response to error.data
      const errorMessage =
        error?.data?.message ||
        error.message ||
        'Failed to set password. Please try again.'
      const isAccountLocked =
        errorMessage?.includes('temporarily locked') ||
        errorMessage?.includes('multiple failed attempts')

      // Check for account lockout error
      if (isAccountLocked) {
        // Use translation for account locked message
        setErrors({ submit: t('resetPassword.errors.accountLocked') })
      } else if (
        errorMessage.includes('expired') ||
        errorMessage.includes('Invalid')
      ) {
        // Check for specific error messages to invalidate token
        setTokenValid(false)
      } else if (
        error.isRateLimit ||
        errorMessage.includes('Rate limit exceeded')
      ) {
        // Check for rate limit
        setErrors({
          submit:
            t('resetPassword.errors.rateLimitExceeded') ||
            'Too many requests. Please try again later.',
        })
      } else {
        // Always show the actual error message
        setErrors({ submit: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestNewLink = () => {
    const tenantUrlName = tenant?.tenanturlname || tenanturlname
    navigate(`/${tenantUrlName}/forgot-password`)
  }

  return (
    <>
      {isLoading && <Loader fullscreen label="Setting password..." size="xl" />}

      <div className="w-full px-6 mt-10 md:max-w-[600px] md:mx-auto">
        <div className="w-full px-6 md:max-w-[600px] md:mx-auto">
          {/* Set Password - START */}
          {!tokenValid ? (
            /* Invalid/Expired Token State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                Link Expired or Invalid
              </h2>
              <p className="text-sm text-theme-secondary mb-6">
                This migration link has expired or is invalid. Migration links
                are valid for 120 minutes only.
              </p>
              <Button variant="primary" onClick={handleRequestNewLink}>
                Request New Migration Link
              </Button>
            </div>
          ) : isSuccess ? (
            /* Success State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                Account Migrated Successfully!
              </h2>
              <p className="text-sm text-theme-secondary mb-6">
                Your password has been set and your account has been
                successfully migrated. You will be redirected to the sign in
                page shortly.
              </p>
              <div className="flex justify-center">
                <Loader size="md" labelColor="text-theme-primary" />
              </div>
            </div>
          ) : (
            /* Set Password Form */
            <form
              className="form-container"
              onSubmit={handleSubmit}
              noValidate
              aria-label="Set Password form"
            >
              <div className="text-center mb-4">
                <h1 className="text-base font-extrabold text-theme-secondary">
                  Set Your Password
                </h1>
                <p className="text-xs text-theme-secondary mt-2">
                  Welcome! Set your password to complete your account migration.
                  Make sure it's strong and secure.
                </p>
              </div>

              {/* Display general error */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* New Password Field */}
                <div className="grid gap-1">
                  <label
                    htmlFor="newPassword"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={errors.newPassword ? 'true' : 'false'}
                    aria-describedby={
                      errors.newPassword ? 'newPassword-error' : undefined
                    }
                  />
                  {errors.newPassword && (
                    <p
                      id="newPassword-error"
                      className="text-xs text-red-500 mt-1"
                    >
                      {errors.newPassword}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 12 characters long and include
                    uppercase, lowercase, number, and special character
                  </p>
                </div>

                {/* Confirm Password Field */}
                <div className="grid gap-1">
                  <label
                    htmlFor="confirmPassword"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.confirmPassword
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    aria-required="true"
                    aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                    aria-describedby={
                      errors.confirmPassword
                        ? 'confirmPassword-error'
                        : undefined
                    }
                  />
                  {errors.confirmPassword && (
                    <p
                      id="confirmPassword-error"
                      className="text-xs text-red-500 mt-1"
                    >
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <Button
                  id="set-password-btn"
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? 'Setting Password...' : 'Set Password'}
                </Button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={handleRequestNewLink}
                    className="text-sm text-theme-primary hover:underline"
                  >
                    Request a new migration link
                  </button>
                </div>
              </div>
            </form>
          )}
          {/* Set Password - END */}
        </div>
      </div>
    </>
  )
}

export default SetPassword
